import { zodResolver } from '@hookform/resolvers/zod';
import { passwordResetEmailRequestSchema } from '@groundly/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useRequestPasswordResetEmail } from '../api/password-resets.js';
import { requestFailedBody, requestRefusal } from '../api/verification-emails.js';
import { useCarriedValues } from '../auth/navigation-state.js';
import { PreAccountGate } from '../auth/PreAccountGate.js';
import { waitBody } from '../auth/signup-errors.js';
import { Button, TextButton } from '../components/Button.js';
import { DangerAlert } from '../components/DangerAlert.js';
import { PageHeading } from '../components/PageHeading.js';
import { TextField } from '../components/TextField.js';
import text from '../components/pre-account-text.module.css';
import { PreAccountCard, PreAccountFootnote, PreAccountFrame } from '../frame/PreAccountFrame.js';

/**
 * Forgot your password: asks for a password reset email. Opened from the log
 * in screen, with the typed address carried over, and from a reset link that
 * can't be used (`?link=invalid`, which adds its alert).
 *
 * The confirmation is true whatever the server found: Groundly sends only when
 * the address has a login, and never says whether it does.
 */
const searchSchema = z.object({ link: z.enum(['invalid']).optional() });

export const Route = createFileRoute('/forgot-password')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  return (
    <PreAccountGate>
      <ForgotPassword />
    </PreAccountGate>
  );
}

interface FormInput {
  email: string;
}

type Alert = { title: string; body: string; role: 'alert' | 'status' };

const INVALID_LINK_ALERT: Alert = {
  title: "This password reset link can't be used",
  body: 'It may have expired or been used already. Enter your email address to get a new one.',
  role: 'status',
};

function ForgotPassword() {
  const { link } = Route.useSearch();
  const carried = useCarriedValues();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [alert, setAlert] = useState<Alert | null>(link === 'invalid' ? INVALID_LINK_ALERT : null);
  const [lastEmail, setLastEmail] = useState(carried.email);

  if (sentTo !== null) {
    return (
      <Sent
        email={sentTo}
        onUseDifferent={() => {
          setLastEmail(sentTo);
          setSentTo(null);
        }}
      />
    );
  }

  return (
    <ForgotPasswordForm
      defaultEmail={lastEmail}
      alert={alert}
      onAlert={setAlert}
      onSent={(email) => {
        setAlert(null);
        setSentTo(email);
      }}
    />
  );
}

function ForgotPasswordForm({
  defaultEmail,
  alert,
  onAlert,
  onSent,
}: {
  defaultEmail: string;
  alert: Alert | null;
  onAlert: (alert: Alert | null) => void;
  onSent: (email: string) => void;
}) {
  const request = useRequestPasswordResetEmail();

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm<FormInput, unknown, { email: string }>({
    resolver: zodResolver(passwordResetEmailRequestSchema),
    defaultValues: { email: defaultEmail },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });

  const email = register('email');

  const onSubmit = handleSubmit((values) => {
    if (request.isPending) return;
    request.mutate(values.email, {
      onSuccess: () => onSent(values.email),
      onError: (error) => {
        const refusal = requestRefusal(error);
        if (refusal.kind === 'field') {
          setError('email', { message: refusal.message });
          setFocus('email');
          return;
        }
        onAlert(
          refusal.kind === 'too-many'
            ? {
                title: 'Too many password reset emails requested',
                body: waitBody(refusal.minutes),
                role: 'alert',
              }
            : {
                title: "Couldn't request the password reset email",
                body: requestFailedBody(refusal.kind),
                role: 'alert',
              },
        );
      },
    });
  });

  return (
    <PreAccountFrame>
      <PreAccountCard
        as="form"
        labelledBy="forgot-password-heading"
        onSubmit={(event) => void onSubmit(event)}
        head={<PageHeading id="forgot-password-heading">Reset your password</PageHeading>}
      >
        {alert === null ? null : (
          <DangerAlert title={alert.title} role={alert.role}>
            {alert.body}
          </DangerAlert>
        )}

        <p className={text.message}>
          Enter the email address you log in with and we&apos;ll send you a link to choose a new
          password.
        </p>

        <TextField
          {...email}
          id="email"
          label="Email address"
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          error={errors.email?.message}
          onChange={(event) => {
            void email.onChange(event);
            if (errors.email !== undefined) void trigger('email');
          }}
          onBlur={(event) => {
            void email.onBlur(event);
            if (dirtyFields.email === true) void trigger('email');
          }}
        />

        <Button
          type="submit"
          variant="primary"
          block
          aria-disabled={request.isPending || undefined}
        >
          {request.isPending ? 'Sending' : 'Send reset link'}
        </Button>
      </PreAccountCard>

      <PreAccountFootnote>
        Remembered it? <Link to="/login">Log in</Link>
      </PreAccountFootnote>
    </PreAccountFrame>
  );
}

function Sent({ email, onUseDifferent }: { email: string; onUseDifferent: () => void }) {
  return (
    <PreAccountFrame>
      <PreAccountCard head={<PageHeading>Check your email</PageHeading>}>
        <p className={text.message}>
          If <span className={text.address}>{email}</span> has a Groundly login, we sent a link to
          it. Open the link to choose a new password. It works for 1 hour.
        </p>
        <p className={text.muted}>Can&apos;t find the email? Check your spam or junk folder.</p>
      </PreAccountCard>

      <PreAccountFootnote>
        <TextButton onClick={onUseDifferent}>Use a different email address</TextButton>
      </PreAccountFootnote>
    </PreAccountFrame>
  );
}
